import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";

import classNames from "classnames";

import { DeviceVideoIcon } from "@jti/brand-icons/device-video";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import { Loader } from "@jti/core-components/components/Loader";
import { isApiErrorWithErrorCode, translateBackendErrorCode } from "@jti/fe-common";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { TruncatedTooltip } from "@jti/ui/components/TruncatedTooltip";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import useLoggedUser from "../../hooks/useLoggedUser";
import { REPLACE_URL_PLACEHOLDER, useLoginRedirectionUrl } from "../../hooks/useLoginRedirectionUrl";
import { firstLetterCapitalize } from "../../utils";
import type { LoyaltyVideoPlayerProps } from "../LoyaltyVideoPlayer";
import { LoyaltyVideoPlayer } from "../LoyaltyVideoPlayer";

import styles from "./LoyaltyVideoPreview.module.scss";

export type LoyaltyVideoPreviewProps = LoyaltyVideoPlayerProps & {
  fullHeight?: boolean;
};

export const LoyaltyVideoPreview = (props: LoyaltyVideoPreviewProps) => {
  const { videoId, fullHeight, ...videoPlayerProps } = props;
  const [isWatchedState, setIsWatchedState] = useState<boolean | null>(null);
  const [points, setPoints] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [{ hasError, errorText }, setErrorState] = useState<{ hasError: boolean; errorText: string | null }>({
    hasError: false,
    errorText: null,
  });
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const [isVideoPlayed, setIsVideoPlayed] = useState<boolean>(false);
  const { __ } = useTranslations();
  const [isLogged] = useLoggedUser();
  const loginUrl = useLoginRedirectionUrl(REPLACE_URL_PLACEHOLDER);

  useEffect(() => {
    if (!videoId || !isLogged) {
      return;
    }

    const fetchVideoData = async () => {
      try {
        setIsLoading(true);
        const user = getUser();
        const response = (
          await LoyaltyApi.init().getArticles({
            ...(user.uuid && user.ciam_token ? { uuid: user.uuid, ciam_token: user.ciam_token } : {}),
            typedIdentifiers: {
              video: [videoId],
            },
          })
        )[0];

        if (response) {
          setPoints(response.value);
          if (isLogged) {
            setIsWatchedState(response.is_watched ?? false);
          }
        }
      } catch (error) {
        console.log(error);
        setErrorState({
          hasError: true,
          errorText: translateBackendErrorCode(
            isApiErrorWithErrorCode(error) ? error.error_code : undefined,
            __("loyalty.loyaltyVideoPreview.errorMessage")
          ),
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideoData();
  }, [videoId, isLogged, __]);

  const handleVideoWatched = () => {
    setIsWatchedState(true);
  };

  const handleIsPlayingChange: Dispatch<SetStateAction<boolean>> = (value) => {
    setIsVideoPlaying(value);
    const resolved = typeof value === "function" ? value(isVideoPlaying) : value;
    if (resolved) {
      setIsVideoPlayed(true);
    }
  };

  const earnedText = __(`loyalty.articleRestrictedContentHeader.${isWatchedState ? "pointsEarned" : "earnPoints"}`, [
    points ?? 0,
  ]);
  const loginText = __("loyalty.loyaltyVideoPreview.loginToEarnPoints");

  const handleLoginLinkClick = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingLoginClick, {
      section: "loyalty-video-preview",
      template_name: "login",
    });
  };

  if (hasError) {
    return (
      <Typography variant="h1" className={styles.error}>
        {__(errorText || "")}
      </Typography>
    );
  }

  return (
    <div className={classNames(styles.wrapper, { [styles.fullHeight]: fullHeight })}>
      <LoyaltyVideoPlayer
        videoId={videoId}
        isWatchTimeTracked={isLogged && !isWatchedState}
        onWatchTimeReached={handleVideoWatched}
        onPlayingChange={handleIsPlayingChange}
        pointsGranted={points ?? 0}
        {...videoPlayerProps}
      >
        {!isVideoPlaying && (isVideoPlayed || !videoPlayerProps.hostedExternally) && (
          <div className={styles.videoOverlay}></div>
        )}
      </LoyaltyVideoPlayer>
      <span data-testid="articleRestrictedContentHeaderPointsVideo" className={styles.earnPointsBlock}>
        <DeviceVideoIcon height={20} width={20} className={isLogged ? undefined : styles.accentIcon} />
        {isLoading ? (
          <Loader data-testid="loadingVideoPreview" size="sm" />
        ) : isLogged ? (
          <TruncatedTooltip
            content={firstLetterCapitalize(earnedText)}
            iconClassName={styles.textTrigger}
            wrapperClassName={styles.tooltipTextWrapper}
          >
            <span className={styles.earnPointsText}>{earnedText}</span>
          </TruncatedTooltip>
        ) : (
          <TruncatedTooltip
            content={firstLetterCapitalize(loginText)}
            iconClassName={styles.textTrigger}
            wrapperClassName={styles.tooltipTextWrapper}
          >
            <LinkBehaviour href={loginUrl} className={styles.loginLink} onClick={handleLoginLinkClick}>
              {loginText}
            </LinkBehaviour>
          </TruncatedTooltip>
        )}
      </span>
    </div>
  );
};
